import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../../../../constants';
import { createPlaceholderUrl } from '../../../../../utils/PlaceholderCreator';
import RacePlannerFilter, { FILTER_CATEGORIES, FILTER_RANKS } from './RacePlannerFilter';

const RacePlannerCharacterSearch = ({
    filteredCharacters, cards, onSelect, onSearchActive,
    filters, onFilterChange, activeFilterCount, totalCharacters, emptyFilters,
}) => {
    const [searchTerm, setSearchTerm]   = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);

    const showResults = searchTerm.length > 0 || activeFilterCount > 0;

    const results = useMemo(() => {
        if (!searchTerm) return filteredCharacters.slice(0, 30);
        const q = searchTerm.toLowerCase();
        return filteredCharacters.filter(c =>
            c.name_en.toLowerCase().includes(q) ||
            c.name_ja.toLowerCase().includes(q)
        ).slice(0, 20);
    }, [filteredCharacters, searchTerm]);

    useEffect(() => {
        onSearchActive(showResults);
    }, [showResults, onSearchActive]);

    const handleSelect = (char) => {
        onSelect(char);
        setSearchTerm('');
        setFiltersOpen(false);
    };

    const handleFilterChange = (key, value) => {
        onFilterChange(prev => ({ ...prev, [key]: value }));
    };

    const clearAll = () => {
        onFilterChange(emptyFilters);
        setSearchTerm('');
    };

    const getThumbnail = (char) => {
        const card = cards.find(c => c.Id_Personaje === char.Id_Personaje && c.reference === 'Original');
        if (!card) return createPlaceholderUrl(100, 100, char.name_en.substring(0, 2));
        const name = char.name_en.replace(/[\\/*?"<>|]/g, '').replace(/ /g, '_');
        const ref  = card.reference.replace(/[\\/*?"<>|]/g, '').replace(/ /g, '_');
        const cos  = (card.title_en || '').replace(/[\\/*?"<>|]/g, '').replace(/ /g, '_');
        return `${API_BASE_URL}/api/thumb?img=/images/character/${name}_${ref}_${cos}.png&width=200`;
    };

    return (
        <div className="w-full max-w-4xl mx-auto space-y-3">

            {/* ── Row 1: Search bar + filter toggle ─────────────────── */}
            <div className="flex gap-3 items-stretch">

                {/* Search input */}
                <div className="relative flex-1">
                    {/* Active glow border */}
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: searchTerm ? [0.4, 1, 0.4] : 0 }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                        className="absolute -inset-[1px] rounded-2xl pointer-events-none">
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#FAB0C0]/50 via-[#48E5B9]/50 to-[#FAB0C0]/50 blur-[3px]" />
                    </motion.div>
                    <div className="relative flex items-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 gap-3 shadow-sm">
                        <svg className="w-4 h-4 shrink-0 transition-colors duration-300"
                            style={{ color: searchTerm ? '#48E5B9' : undefined }}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Buscar Umamusume..."
                            className="w-full bg-transparent border-none outline-none text-sm font-semibold placeholder:text-gray-300 dark:placeholder:text-gray-600 text-gray-900 dark:text-white"
                        />
                        <AnimatePresence>
                            {searchTerm && (
                                <motion.button initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                                    onClick={() => setSearchTerm('')}
                                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0">
                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Filter toggle button */}
                <button
                    onClick={() => setFiltersOpen(v => !v)}
                    className={`
                        relative flex items-center gap-2 px-4 rounded-2xl border font-bold text-xs
                        transition-all duration-300 shadow-sm shrink-0
                        ${filtersOpen || activeFilterCount > 0
                            ? 'bg-[#48E5B9] text-black border-[#48E5B9]'
                            : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-[#48E5B9]/50'
                        }
                    `}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M7 8h10M10 12h4" />
                    </svg>
                    <span>Filtros</span>
                    {activeFilterCount > 0 && (
                        <span className={`
                            w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center
                            ${filtersOpen ? 'bg-black/20 text-black' : 'bg-[#48E5B9] text-black'}
                        `}>
                            {activeFilterCount}
                        </span>
                    )}
                </button>
            </div>

            {/* ── Row 2: Filter Panel ────────────────────────────────── */}
            <AnimatePresence>
                {filtersOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, y: -8 }}
                        animate={{ opacity: 1, height: 'auto', y: 0 }}
                        exit={{ opacity: 0, height: 0, y: -8 }}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-md relative overflow-hidden">
                            
                            {/* Subtle background ambient glow if filters are active */}
                            <AnimatePresence>
                                {activeFilterCount > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className="absolute -top-20 -right-20 w-64 h-64 bg-[#48E5B9]/5 rounded-full blur-3xl pointer-events-none"
                                    />
                                )}
                            </AnimatePresence>

                            {/* Componente del Filtro extraído */}
                            <RacePlannerFilter filters={filters} onFilterChange={handleFilterChange} />

                            {/* Footer */}
                            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                    {activeFilterCount > 0
                                        ? `${filteredCharacters.length} de ${totalCharacters} personajes`
                                        : `${totalCharacters} personajes disponibles`
                                    }
                                </span>
                                {activeFilterCount > 0 && (
                                    <button onClick={clearAll}
                                        className="text-[10px] font-bold text-rose-400 hover:text-rose-500 transition-colors uppercase tracking-wider">
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Row 3: Results ────────────────────────────────────── */}
            <AnimatePresence>
                {showResults && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg overflow-hidden"
                    >
                        {/* Results header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                {results.length} resultado{results.length !== 1 ? 's' : ''}
                                {activeFilterCount > 0 && ` · ${activeFilterCount} filtro${activeFilterCount !== 1 ? 's' : ''} activo${activeFilterCount !== 1 ? 's' : ''}`}
                            </span>
                            {/* Active filter summary chips */}
                            <div className="flex gap-1 flex-wrap justify-end">
                                {FILTER_CATEGORIES.flatMap(cat => cat.items).filter(i => filters[i.key]).map(item => {
                                    const fval = filters[item.key];
                                    const rank = typeof fval === 'object' ? fval.rank : fval;
                                    const op   = typeof fval === 'object' ? fval.op : '>=';
                                    const opChar = op === '>=' ? '≥' : op === '==' ? '=' : '≤';
                                    return (
                                        <span key={item.key}
                                            className="px-2 py-0.5 rounded-[10px] text-[9px] font-black bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border border-black/5 dark:border-white/5 shadow-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5"
                                        >
                                            <span className="uppercase tracking-widest text-[7px]">{item.label}</span>
                                            <span style={{ color: FILTER_RANKS.indexOf(rank) <= 1 ? '#F87171' : '#48E5B9' }} className="text-[10px] tracking-tighter">
                                                {opChar}{rank}
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Grid */}
                        {results.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-300 dark:text-gray-600 text-xs uppercase tracking-widest font-bold">
                                    Sin resultados
                                </p>
                            </div>
                        ) : (
                            <div className="p-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 max-h-72 overflow-y-auto">
                                {results.map((char, i) => (
                                    <motion.button
                                        key={char.Id_Personaje}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: Math.min(i * 0.02, 0.3) }}
                                        whileHover={{ y: -4, scale: 1.06 }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() => handleSelect(char)}
                                        className="flex flex-col items-center group p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                                    >
                                        <div className="relative w-12 h-12 mb-1.5">
                                            <div className="absolute inset-0 rounded-full bg-[#48E5B9]/20 blur-lg scale-0 group-hover:scale-100 transition-transform duration-400" />
                                            <img
                                                src={getThumbnail(char)}
                                                alt={char.name_en}
                                                className="relative w-full h-full rounded-full object-cover object-top ring-1 ring-gray-200 dark:ring-white/10 group-hover:ring-[#48E5B9]/60 transition-all z-10 bg-gray-100 dark:bg-gray-800"
                                                onError={e => { e.target.src = createPlaceholderUrl(100, 100, char.name_en.substring(0, 2)); }}
                                            />
                                        </div>
                                        <span className="text-[8px] font-bold text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors text-center uppercase tracking-wide truncate w-full leading-tight">
                                            {char.name_en}
                                        </span>
                                    </motion.button>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RacePlannerCharacterSearch;
